import { useStencilContext } from "@amzn/stencil-react-components/context";
import { Container, Spacer } from "@amzn/stencil-react-components/layout";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { H1 } from "@amzn/stencil-react-components/text";
import { type CSSProperties, useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AivaBanner } from "@/components/AIAutomation";
import {
  type JourneyConsentState,
  resolveJourneyConsent,
} from "@/components/AIAutomation/journey-consent-gate";
import { ChatbotFlyout } from "@/components/Chatbot/ChatbotFlyout";
import {
  CHAT_SUPPORT_FLYOUT_INSET_PROPERTY,
  CHAT_SUPPORT_FLYOUT_WIDTH_PX,
  ChatSupportFlyout,
  GetSupportFlyout,
} from "@/components/GetSupport";
import HomeContent from "@/components/HomeContent/HomeContent";
import OnboardingPageLayout from "@/components/OnboardingPageLayout";
import ProgressRail from "@/components/ProgressRail";
import { useObserveSession } from "@/hooks/useObserveSession";
import { useSelectedLanguage } from "@/hooks/useSelectedLanguage";
import { useVpnPreferenceSeed } from "@/hooks/useVpnPreferenceSeed";
import { RTL_LANGUAGES } from "@/i18n/locales";
import { AppContext } from "@/stores/appStore";
import { AivaActivityProvider } from "@/stores/contexts/AivaActivityContext";
import { AivaBannerCollapseProvider } from "@/stores/contexts/AivaBannerContext";

import Header from "../Header";

const devOrigins = ["https://0.0.0.0:8000", "https://dev.cwb.it.a2z.com:8000"];

/**
 * The Day1 OnBoarding Page which contains different steps to help new hires setting up their laptops.
 *
 * TODO: Get the current progress and determine what to display to the user based on the progress. Keeping this here as POC for using API.
 */
export default function Home(): JSX.Element {
  const intl = useIntl();
  const currentLocale = useSelectedLanguage();
  const { setIsRTL } = useStencilContext();
  const isRTL = RTL_LANGUAGES.includes(currentLocale);
  const { state } = useContext(AppContext);

  /*
   * Resolved before the first render, not corrected after it: otherwise the consent screen
   * mounts for a commit on every reload and announces itself to a hire who already answered.
   */
  const [journeyConsent, setJourneyConsent] = useState<JourneyConsentState>(
    resolveJourneyConsent
  );
  /*
   * Held in state rather than read during render, so the value keeps one identity while
   * the answer is unchanged. A fresh object each render would restart the observe
   * effect on every render instead of on a real change.
   *
   * Runs after every render rather than keyed on the step, because the hire answers on
   * the step they are already on and a dependency list would miss the grant that starts
   * the loop. It cannot chain: an unchanged answer returns the previous object, so no
   * re-render follows.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const resolved = resolveJourneyConsent();
    setJourneyConsent((previous) =>
      resolved.kind === previous.kind ? previous : resolved
    );
  });

  /*
   * `currentStep` is not a reliable answer to "is the hire still working through this?".
   * Completing the last step sets it to undefined, and the next REBUILD_STEPS -- which a
   * feature-flag refetch dispatches -- clamps the index back onto the last step and
   * resurrects it. The step number is what survives that, so it is what decides whether
   * there is still a journey to record.
   */
  const hasFinishedJourney = state.currentStepNum > state.steps.length;

  /*
   * Reported by the step area, the only thing that knows which of its screens is showing. The rail
   * goes for the consent screen, and while readiness is still resolving on a first step that could
   * still ask; blocking-error screens keep it.
   */
  const [isShowingTakeover, setIsShowingTakeover] = useState(false);

  const observeSession = useObserveSession(
    journeyConsent,
    hasFinishedJourney ? undefined : state.currentStep?.id,
    window.electronAPI?.startObserveSession,
    window.electronAPI?.stopObserveSession,
    window.electronAPI?.setObserveStep
  );

  useVpnPreferenceSeed();

  useEffect(() => {
    setIsRTL(isRTL);
  }, [isRTL, setIsRTL]);

  useEffect(() => {
    if (
      // If window.opener is not null, it indicates this is a popup because the info in window.opener contains info on the parent who opened it.
      window.opener &&
      // Don't do this in development because it's irritating.
      !devOrigins.includes(window.origin)
    ) {
      window.resizeTo(window.screen.availWidth, window.screen.availHeight);
    }

    document.title = intl.formatMessage({ id: "page-title" });
    document.documentElement.lang = intl.locale;
  }, [intl]);

  /*
   * One number insets the page content and is published for anything the inset cannot reach. The
   * Aiva banner is `position: fixed`, so padding does not move it and it offsets by the property
   * instead, keeping its vertical place while the flyout is open.
   */
  const isChatSupportFlyoutOpen = state.chatSupportFlyoutVisible;
  /*
   * The chatbot covers the same right-hand strip, so a fixed surface owes it the same offset even
   * though only the support flyout insets the page's own content. Published off one flag alone, the
   * banner docked straight over an open chatbot panel. The two are mutually exclusive in the reducer,
   * so this is still one width rather than a sum.
   */
  const isRightStripCovered =
    isChatSupportFlyoutOpen || state.chatbotFlyoutVisible;
  const flyoutInset = isRightStripCovered
    ? `${CHAT_SUPPORT_FLYOUT_WIDTH_PX}px`
    : "0px";
  const flyoverPad = {
    ...(isChatSupportFlyoutOpen
      ? { paddingRight: `${CHAT_SUPPORT_FLYOUT_WIDTH_PX}px` }
      : {}),
    [CHAT_SUPPORT_FLYOUT_INSET_PROPERTY]: flyoutInset,
  } as CSSProperties;

  return (
    <>
      {/*Mount Chat Support flyout component in the DOM to allow it to be "resumed" instead of un/re loading the component every time it becomes invisible.*/}
      <ChatSupportFlyout />
      {/*Mount the chatbot flyout component in the DOM*/}
      <ChatbotFlyout />
      {/*Mount the Get Support flyout component in the DOM*/}
      <GetSupportFlyout />
      <Container
        isFullWidth={true}
        isCentered={true}
        style={flyoverPad}
        dataTestId="homeContainer"
      >
        <Header />
        <PageContainer>
          {/* No rail while a takeover is up: it would place the hire on identity
              verification when something else has replaced the wizard. */}
          <OnboardingPageLayout
            rail={isShowingTakeover ? undefined : <ProgressRail />}
          >
            <H1>
              <FormattedMessage id="page-heading" />
            </H1>
            <Spacer height="S500" />
            {/* The step publishes what Aiva is doing and the banner reports it. The collapse
                provider is kept for the overlay's resting form, which nothing renders today. */}
            <AivaActivityProvider observe={observeSession}>
              <AivaBannerCollapseProvider>
                <AivaBanner />
                <HomeContent
                  journeyConsent={journeyConsent}
                  onJourneyConsentAnswered={() =>
                    setJourneyConsent(resolveJourneyConsent())
                  }
                  onTakeoverChange={setIsShowingTakeover}
                />
              </AivaBannerCollapseProvider>
            </AivaActivityProvider>
          </OnboardingPageLayout>
        </PageContainer>
      </Container>
    </>
  );
}
